import type { Contract, DocumentRecord, Milestone } from '../../types';
import { computePlan, formatKronor, newPlanRow, type PlanRow, type PlanState } from './milestonePlan';
import { isRichTextEmpty, type AssignmentValues } from './useContractWizardDraft';

/**
 * Edit mode: what state the contract is in when the wizard opens.
 * - draft: never sent (or reopened) — every change is saved as usual.
 * - signing: the company started signing but has not signed. The provider
 *   invites the signers in order, so nothing has reached the customer; the
 *   first change silently stops that signing (ClickUp 869fnf0qc).
 * - sent: signed by the company and out with the customer; the API locks it,
 *   so changes are kept in the wizard until the company withdraws the sending
 *   (then it is a draft again).
 * - signed: signed by both parties — read-only; changes are made with an ÄTA.
 */
export type WizardEditStatus = 'draft' | 'signing' | 'sent' | 'signed';

const ACTIVE_SIGNING = ['sent', 'pending_signature'];
const MANUAL_SIGNED = ['signed_by_company', 'signed_by_person', 'signed_by_both'];

/**
 * Which edit view a fixed-price contract opens in, or null when the wizard
 * cannot handle it (e.g. locked by a manual signing flow) and the contract page
 * is used instead. Mirrors the API's lock rule (isContractLockedForEditing).
 */
export function wizardEditStatus(contract: Contract): WizardEditStatus | null {
  if (contract.contractType !== 'fixed') return null;
  if ((contract.companySigned && contract.personSigned) || contract.signingStatus === 'signed') return 'signed';
  if (contract.adminUnlocked || contract.draftStatus === 'draft_rejected') return 'draft';
  if (ACTIVE_SIGNING.includes(contract.signingStatus ?? '') && !contract.personSigned) {
    return contract.companySigned ? 'sent' : 'signing';
  }
  if (MANUAL_SIGNED.includes(contract.draftStatus) || contract.companySigned || contract.personSigned) return null;
  return 'draft';
}

/** Plan state for an existing contract, in the wizard's own format (kronor as typed text). */
export function planStateFromContract(contract: Contract, milestones: Milestone[]): PlanState {
  const pricingMode = contract.pricingMode ?? 'top_down';
  const stages = milestones
    .filter(milestone => (milestone.itemType ?? 'milestone') === 'milestone' && milestone.deliveryStatus !== 'paid')
    .sort((left, right) => left.milestoneNumber - right.milestoneNumber);
  const rows: PlanRow[] = stages.map(milestone => ({
    ...newPlanRow(),
    id: milestone.id,
    description: milestone.description ?? '',
    amount: String(pricingMode === 'bottom_up' ? milestone.amount : milestone.afterRotAmount),
    rotAmount: pricingMode === 'bottom_up' ? String(milestone.rotAmount) : '',
    endDate: milestone.endDate?.slice(0, 10) ?? '',
  }));
  const gross = Number(contract.contractAmount) || 0;
  const afterRot = Number(contract.afterRotAmount) || 0;
  return {
    pricingMode,
    distribution: 'amount',
    total: pricingMode === 'top_down' && gross ? String(gross) : '',
    afterRot: pricingMode === 'top_down' && afterRot && afterRot !== gross ? String(afterRot) : '',
    rows: rows.length > 0 ? rows : [newPlanRow()],
  };
}

export interface WizardSnapshot {
  values: AssignmentValues;
  attachments: Pick<DocumentRecord, 'id' | 'fileName' | 'originalName'>[];
  plan: PlanState;
}

export interface ContractChange {
  section: 'assignment' | 'pricing';
  /** i18n key under `contracts.wizard.changes`. */
  key: string;
  params?: Record<string, string | number>;
}

const html = (value: string) => (isRichTextEmpty(value) ? '' : value.trim());
const fileName = (doc: WizardSnapshot['attachments'][number]) => doc.originalName || doc.fileName;

/**
 * "Ändringar sedan version N": what differs between the version that was sent
 * (the snapshot taken when the wizard opened) and the wizard's current state.
 * Stages are matched by id; a stage without one is new.
 */
export function diffContract(original: WizardSnapshot, current: WizardSnapshot): ContractChange[] {
  const changes: ContractChange[] = [];
  const assignment = (key: string, params?: ContractChange['params']) => changes.push({ section: 'assignment', key, params });
  const pricing = (key: string, params?: ContractChange['params']) => changes.push({ section: 'pricing', key, params });

  const before = original.values;
  const after = current.values;
  if (before.contractName.trim() !== after.contractName.trim()) assignment('contractName');
  if (html(before.assignmentDescription) !== html(after.assignmentDescription)) assignment('assignmentDescription');
  if (before.referToAttachments !== after.referToAttachments) assignment('referToAttachments');
  if (html(before.otherDescription) !== html(after.otherDescription)) assignment('otherDescription');

  const beforeFiles = new Set(original.attachments.map(doc => doc.id));
  const afterFiles = new Set(current.attachments.map(doc => doc.id));
  current.attachments.filter(doc => !beforeFiles.has(doc.id)).forEach(doc => assignment('attachmentAdded', { name: fileName(doc) }));
  original.attachments.filter(doc => !afterFiles.has(doc.id)).forEach(doc => assignment('attachmentRemoved', { name: fileName(doc) }));

  const planBefore = computePlan(original.plan);
  const planAfter = computePlan(current.plan);
  if (original.plan.pricingMode !== current.plan.pricingMode) pricing('pricingMode');
  if (planBefore.gross !== planAfter.gross) {
    pricing('gross', { from: formatKronor(planBefore.gross), to: formatKronor(planAfter.gross) });
  }
  if (planBefore.afterRot !== planAfter.afterRot) {
    pricing('afterRot', { from: formatKronor(planBefore.afterRot), to: formatKronor(planAfter.afterRot) });
  }

  const beforeRows = new Map<string, { row: PlanRow; amount: number }>();
  original.plan.rows.forEach((row, index) => {
    if (row.id) beforeRows.set(row.id, { row, amount: planBefore.stageAfterRot[index] });
  });
  current.plan.rows.forEach((row, index) => {
    const number = index + 1;
    const previous = row.id ? beforeRows.get(row.id) : undefined;
    if (!previous) {
      pricing('stageAdded', { number, description: row.description.trim() });
      return;
    }
    const amount = planAfter.stageAfterRot[index];
    if (previous.amount !== amount) {
      pricing('stageAmount', { number, from: formatKronor(previous.amount), to: formatKronor(amount) });
    }
    if (previous.row.description.trim() !== row.description.trim()) pricing('stageDescription', { number });
    if (previous.row.endDate !== row.endDate) pricing('stageEndDate', { number, to: row.endDate || '–' });
  });
  const kept = new Set(current.plan.rows.flatMap(row => (row.id ? [row.id] : [])));
  const removed = [...beforeRows.keys()].filter(id => !kept.has(id)).length;
  if (removed > 0) pricing('stagesRemoved', { count: removed });

  return changes;
}

/**
 * Per stage marker in step 2: "Ny", "Tidigare 55 000 kr" (amount changed) or
 * "Ändrad" (text or date changed). Keyed by the row's React key.
 */
export function stageMarkers(original: PlanState, current: PlanState): Record<string, { key: string; amount?: string }> {
  const planBefore = computePlan(original);
  const planAfter = computePlan(current);
  const beforeRows = new Map<string, number>();
  original.rows.forEach((row, index) => {
    if (row.id) beforeRows.set(row.id, index);
  });
  const markers: Record<string, { key: string; amount?: string }> = {};
  current.rows.forEach((row, index) => {
    const beforeIndex = row.id ? beforeRows.get(row.id) : undefined;
    if (beforeIndex === undefined) {
      markers[row.key] = { key: 'new' };
      return;
    }
    const previous = original.rows[beforeIndex];
    if (planBefore.stageAfterRot[beforeIndex] !== planAfter.stageAfterRot[index]) {
      markers[row.key] = { key: 'previousAmount', amount: formatKronor(planBefore.stageAfterRot[beforeIndex]) };
    } else if (previous.description.trim() !== row.description.trim() || previous.endDate !== row.endDate) {
      markers[row.key] = { key: 'changed' };
    }
  });
  return markers;
}
